import React from "react";
import { useSelector } from "react-redux";
import { format, utcToZonedTime } from "date-fns-tz";
import PT from "prop-types";
import get from "lodash/get";
import { SocialShare } from "@quintype/components";
import {
  AuthorBox,
  AuthorDetails,
  FollowUs,
  Headline,
  MultipleAuthorDetails,
  PublishDetails,
  SocialShareIcons,
  SubHeadline,
} from "../atoms";
import { HeroImage } from "../../atoms/HeroImage";
import AdWrapper from "../atoms/AdWrapper";
import { DefaultStoryCard } from "../DefaultStoryCard";
import CustomStoryElement from "../custom-story-elements";
import ShareMenu from "./ShareMenu";
import KeyEvents from "./KeyEvents";
import "./live-blog.m.css";
import Newsletter from "../../atoms/Newsletter";

function StoryCard(props) {
  const { card, story, counter } = props;
  const attribute = get(card, ["metadata", "attributes", "cardtype", "0"], null);
  if (attribute) {
    return <CustomStoryElement card={card} attribute={attribute} story={story} counter={counter} />;
  }
  return <DefaultStoryCard card={props.card} story={props.story} counter={counter} />;
}

StoryCard.propTypes = {
  card: PT.object,
  story: PT.object,
};

const CardsWithAds = ({ story, isLiveblogClosed }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const mountAt = get(qtState, ["config", "mountAt"], "");
  const timezone = useSelector((state) => get(state, ["qt", "data", "timezone"], "Asia/Kolkata"));
  const storyId = get(story, ["id"], "");
  const counter = { ads: 0, paragraphs: 0 };

  const getTimeStamp = (date) => {
    const zonedTime = utcToZonedTime(date, timezone);
    const formatZonedTime = zonedTime && format(zonedTime, "hh:mm a, d MMMM yyyy", { timeZone: timezone });
    return <time dateTime={formatZonedTime}>{formatZonedTime}</time>;
  };

  return story?.cards?.map((card, index) => {
    const { "card-added-at": cardAddedAt } = card;
    const cardId = get(card, ["id"], "");

    const storyUrl = new URL(story.url);
    const shareUrl = encodeURI(storyUrl.origin + `${mountAt}${storyUrl.pathname}#${story.slug}-${cardId}`);
    return (
      <div key={index} id={`${story?.slug}-${cardId}`}>
        <div styleName="card-share">
          <div styleName="event-time-line">
            {!isLiveblogClosed && <div styleName="red-dot"></div>}
            {getTimeStamp(cardAddedAt)}
          </div>
          <div id={`card-share-${cardId}_${storyId}`} className="content-style">
            <SocialShare template={ShareMenu} fullUrl={shareUrl} title={story.headline} />
          </div>
        </div>
        <StoryCard key={card.id} card={card} story={story} isliveblog={true} counter={counter} />
      </div>
    );
  });
};

function LiveBlogTemplate({ story }) {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isLiveblogClosed = get(story, ["metadata", "is-closed"], false);
  const timezone = useSelector((state) => get(state, ["qt", "data", "timezone"], "Asia/Kolkata"));
  const adsConfig = get(qtState, ["config", "ads-config"], {});
  const storyPageLhsAd = get(adsConfig, ["slots", "live_blog_lhs_ad"], {});
  const template = get(story, ["story-template"], "");
  const authorBoxAttr = get(story, ["metadata", "story-attributes", "authorbox", "0"], "hide");
  const liveBlogIntro = get(story, ["metadata", "live-blog-introduction"], "");
  const storyId = get(story, ["id"], "");

  const keyEvents = () => {
    const cards = get(story, ["cards"], []);
    const cardsWithKeyEvents = cards?.filter((card) => get(card, ["metadata", "attributes", "key-event"], ""));
    return (
      cardsWithKeyEvents?.length > 0 && (
        <div styleName="key-events" id={`key-events-${storyId}`}>
          <p styleName="key-event-headline">Highlights</p>
          <KeyEvents story={story} />
        </div>
      )
    );
  };

  return (
    <div>
      <div styleName="meta-data-section">
        {!isLiveblogClosed && (
          <div styleName="live">
            <span styleName="live-icon"></span>
            <span>LIVE</span>
          </div>
        )}
        <Headline headline={story?.headline} />
        <SubHeadline subheadline={story?.subheadline} />
        <PublishDetails story={story} template="story" timezone={timezone} />
        <AuthorDetails story={story} />
        <div styleName="tags">
          {story.tags?.map((tag) => (
            <span>{tag.name}</span>
          ))}
        </div>
        <div styleName="read-time-social-share-div">
          <span styleName="follow-us-share">
            <FollowUs />
            <SocialShareIcons story={story} imageDimension={{ width: "34px", height: "34px", viewbox: "0 0 34 34" }} />
          </span>
        </div>
      </div>
      <div styleName="hero-image-section">
        <HeroImage story={story} aspectRatio={[[4, 3]]} isStoryPageImage />
      </div>
      <div styleName="content-section">
        {liveBlogIntro && <div styleName="lb-intro">{liveBlogIntro}</div>}
        {keyEvents()}
        <div>{story?.cards && <CardsWithAds story={story} isLiveblogClosed={isLiveblogClosed} />}</div>
        {authorBoxAttr.toLowerCase() === "show" && <AuthorBox story={story} />}
        {story.authors?.length > 3 && (
          <div styleName="multiple-authors">
            <MultipleAuthorDetails story={story} />
          </div>
        )}
        <Newsletter />
        {storyPageLhsAd.ad_unit && (
          <AdWrapper
            adStyleName="text-story-ad-slot-size-300x250"
            id={`${template}-lhs-ad`}
            path={storyPageLhsAd.ad_unit}
            size={storyPageLhsAd.sizes}
            viewPortSizeMapping={storyPageLhsAd.view_port_size_mapping}
            isLhsAd={true}
          />
        )}
      </div>
    </div>
  );
}

export default LiveBlogTemplate;
